import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function EnquiryForm({ type = "kontakt" }) {
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const [models, setModels] = useState([]);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", model: params.get("modell") || "",
    company: "", preferred_date: "", message: "",
  });

  useEffect(() => {
    api.get("/models").then((r) => setModels(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, type });
      toast.success(
        type === "probefahrt" ? t("form.successProbefahrt") : t("form.successKontakt")
      );
      setForm({ name: "", email: "", phone: "", model: "", company: "", preferred_date: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid={`${type}-form`}>
      <div>
        <Label htmlFor={`${type}-name`}>{t("form.name")} *</Label>
        <Input id={`${type}-name`} data-testid={`${type}-name-input`} required value={form.name}
               onChange={set("name")} className="rounded-sm mt-1.5" placeholder={t("form.namePlaceholder")} />
      </div>
      <div>
        <Label htmlFor={`${type}-email`}>{t("form.email")} *</Label>
        <Input id={`${type}-email`} data-testid={`${type}-email-input`} type="email" required value={form.email}
               onChange={set("email")} className="rounded-sm mt-1.5" placeholder="name@beispiel.ch" />
      </div>
      <div>
        <Label htmlFor={`${type}-phone`}>{t("form.phone")}</Label>
        <Input id={`${type}-phone`} data-testid={`${type}-phone-input`} value={form.phone}
               onChange={set("phone")} className="rounded-sm mt-1.5" placeholder="+41 ..." />
      </div>
      {type === "flotte" ? (
        <div>
          <Label htmlFor={`${type}-company`}>{t("form.company")} *</Label>
          <Input id={`${type}-company`} data-testid={`${type}-company-input`} required value={form.company}
                 onChange={set("company")} className="rounded-sm mt-1.5" placeholder="Firmenname" />
        </div>
      ) : (
        <div>
          <Label>{t("form.model")}</Label>
          <Select value={form.model} onValueChange={set("model")}>
            <SelectTrigger data-testid={`${type}-model-select`} className="rounded-sm mt-1.5 bg-white">
              <SelectValue placeholder={t("form.modelPlaceholder")} />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {models.map((m) => (
                <SelectItem key={m.slug} value={m.slug} data-testid={`${type}-model-option-${m.slug}`}>
                  {m.name}
                </SelectItem>
              ))}
              <SelectItem value="beratung">{t("form.consultation")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {type === "probefahrt" && (
        <div className="sm:col-span-2">
          <Label htmlFor="probefahrt-date">{t("form.date")}</Label>
          <Input id="probefahrt-date" data-testid="probefahrt-date-input" type="date"
                 value={form.preferred_date} onChange={set("preferred_date")} className="rounded-sm mt-1.5" />
        </div>
      )}
      <div className="sm:col-span-2">
        <Label htmlFor={`${type}-message`}>{t("form.message")}</Label>
        <Textarea id={`${type}-message`} data-testid={`${type}-message-input`} rows={4} value={form.message}
                  onChange={set("message")} className="rounded-sm mt-1.5"
                  placeholder={t("form.messagePlaceholder")} />
      </div>
      <div className="sm:col-span-2">
        <Button type="submit" disabled={sending} data-testid={`${type}-submit-btn`}
                className="rounded-md bg-[#0A2540] hover:bg-slate-800 text-white px-8 py-3 w-full sm:w-auto">
          {sending ? t("form.sending") : t(`form.submit${type.charAt(0).toUpperCase() + type.slice(1)}`)}
        </Button>
        <p className="text-xs text-slate-500 mt-3">
          {t("form.privacy")}
        </p>
      </div>
    </form>
  );
}
